import { css, cx } from "@emotion/css";
import { resetStyles, Tooltip } from "@octopusdeploy/design-system-components";
import { borderRadius, borderWidth, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { DeploymentStepResource } from "@octopusdeploy/octopus-server-client";
import { SpecialVariables } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { useActionTemplatesFromContext } from "~/areas/projects/components/Process/Contexts/ProcessActionTemplatesContextProvider";
import getActionLogoUrl from "~/areas/projects/components/getActionLogoUrl";
import { StepRolling } from "~/components/Images/Process/StepRolling";
export interface ProcessTemplateProcessPreviewProps {
    steps: DeploymentStepResource[];
}
export function ProcessTemplateProcessPreview({ steps }: ProcessTemplateProcessPreviewProps) {
    return (<div className={styles.processSection}>
            {steps.map((step) => (<div key={step.Id}>{step.Actions.length === 1 ? <RenderStep action={step.Actions[0]}/> : <RenderParentStep step={step}/>}</div>))}
        </div>);
}
function RenderStep({ action }: {
    action: DeploymentStepResource["Actions"][0];
}) {
    const actionTemplates = useActionTemplatesFromContext();
    const actionTemplate = actionTemplates.find((x) => x.Type === action.ActionType);
    const actionTypeName = actionTemplate ? actionTemplate.Name : action.ActionType;
    return (<div className={styles.stepContainer}>
            <Tooltip content={actionTypeName}>
                <div className={cx(styles.iconContainer, { [styles.iconDisabled]: action.IsDisabled })}>
                    <img className={styles.icon} src={getActionLogoUrl(action)} alt="Action logo"/>
                </div>
            </Tooltip>
            <div className={cx(styles.stepDetails, { [styles.stepDisabled]: action.IsDisabled })}>{action.Name}</div>
        </div>);
}
function RenderParentStep({ step }: {
    step: DeploymentStepResource;
}) {
    const maxParallelism = step.Properties[SpecialVariables.Action.MaxParallelism];
    const showWindowSize = maxParallelism ? maxParallelism.toString().length > 0 : false;
    const parentStepLabel = React.useMemo(() => showWindowSize ? (<span>Rolling deployment</span>) : (<span>
                    Multi-step deployment across
                    <br />
                    deployment targets
                </span>), [showWindowSize]);
    return (<div className={styles.parentStepContainer}>
            <div className={styles.parentStepDetails}>
                <div className={styles.parentIconContainer}>
                    <Tooltip content={parentStepLabel}>
                        <StepRolling height="24px"/>
                    </Tooltip>
                </div>
                <div className={styles.stepDetails}>{step.Name}</div>
            </div>
            <ul className={styles.childActions}>
                {step.Actions.map((action) => (<li key={action.Id}>
                        <RenderStep action={action}/>
                    </li>))}
            </ul>
        </div>);
}
const stepContainerBase = {
    border: `${borderWidth[1]} solid ${themeTokens.color.processStep.border.default}`,
    backgroundColor: themeTokens.color.processStep.background.primary.default,
    borderRadius: borderRadius.medium,
    boxShadow: themeTokens.shadow.extraSmall,
    padding: space[8],
    display: "flex",
};
const iconContainerBase = {
    background: themeTokens.color.background.primary.default,
    borderRadius: borderRadius.extraSmall,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "24px",
    width: "24px",
    flexShrink: 0,
};
const styles = {
    processSection: css({
        padding: space[24],
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        borderRadius: borderRadius.medium,
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        backgroundSize: "52px 34px",
        backgroundColor: `rgb(from ${themeTokens.color.processWrapper.background.primary} r g b / 0.3)`,
        backgroundImage: `radial-gradient(2px circle at 13px 33px, ${themeTokens.color.processWrapper.background.secondary} 50%, transparent), radial-gradient(2px circle at 39px 17px, ${themeTokens.color.processWrapper.background.secondary} 50%, transparent)`,
    }),
    stepContainer: css({
        ...stepContainerBase,
        alignItems: "center",
        display: "flex",
        gap: space[8],
        flex: 1,
    }),
    parentStepContainer: css({
        ...stepContainerBase,
        flex: 1,
        flexDirection: "column",
        padding: space[8],
    }),
    parentStepDetails: css({
        display: "flex",
        alignItems: "center",
        gap: space[8],
        paddingBottom: space[8],
    }),
    iconContainer: css({
        ...iconContainerBase,
    }),
    parentIconContainer: css({
        ...iconContainerBase,
        backgroundColor: colorScales.blue[600],
    }),
    icon: css({
        objectFit: "scale-down",
        width: "100%",
    }),
    iconDisabled: css({
        opacity: 0.5,
    }),
    stepDetails: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
    }),
    stepDisabled: css({
        textDecoration: "line-through",
        color: themeTokens.color.processStep.text.disabled,
    }),
    childActions: css({
        ...resetStyles.list,
        display: "flex",
        gap: space[8],
        flexDirection: "column",
    }),
};
